import { useEffect, useState, useCallback } from 'react';
import { useSnackbar } from 'notistack';

import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractTranslatedApiErrorMessage } from '../../shared/utils/translate-api-error.util';
import { User } from '../types/user.type';
import { ADMIN_USERS_API_PATHS } from '../constants/users-paths.constants';

type UseAdminGetUserByIdResult = {
  isLoading: boolean;
  isRefetching: boolean;
  user: User | null;
  refetch: () => Promise<void>;
};

const INITIAL_STATE: Omit<UseAdminGetUserByIdResult, 'refetch'> = {
  isLoading: true,
  isRefetching: false,
  user: null,
};

export function useAdminGetUserById({ userId }: { userId: string }): UseAdminGetUserByIdResult {
  const { enqueueSnackbar } = useSnackbar();
  const [result, setResult] = useState(INITIAL_STATE);

  const refetch = useCallback(async (): Promise<void> => {
    setResult((previous) => (previous.isLoading ? previous : { ...previous, isRefetching: true }));

    try {
      const response = await HttpClient.get<User | undefined>(ADMIN_USERS_API_PATHS.GET_USER_BY_ID(userId));

      setResult({ isLoading: false, isRefetching: false, user: response ?? null });
    } catch (error) {
      enqueueSnackbar(
        extractTranslatedApiErrorMessage(error, "Une erreur est survenue lors de la récupération de l'utilisateur"),
        { variant: 'error' }
      );
      setResult((previous) => ({ ...previous, isLoading: false, isRefetching: false }));
    }
  }, [userId, enqueueSnackbar]);

  useEffect(() => {
    setResult(INITIAL_STATE);
  }, [userId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { ...result, refetch };
}
